import {
  SearchParamsParser,
  ParamType,
} from "@zerolight-core/search-params-parser";

interface IUrlArgs {
  parentPath?: string
  shortcode?: string
  shortcodeService: string
  starterPack?: string
  model?: string
  from?: string
  micrositeId?: string
  shareId?: string

  assetService: string
  timeout: number
  debug: boolean

  locale: string
  showTranslationIds: boolean

  localStream?: string
  forceCacheMiss: boolean
  force2D: boolean
  force3D: boolean

  overrideDate?: Date

  lazyLoad: boolean
  showPrices: boolean
  itemsAutoApply: boolean
  generateAssets: boolean

  sharing: boolean
  shareFile: false

  configId?: string
  interiorInvertH: boolean
  interiorInvertV: boolean

  focusedStreamInteraction?: boolean

  check3DUpgrade: boolean
  check3DUpgradeRegion: boolean
  min3DTimeZone: number
  max3DTimeZone: number
  lang3D: string
  check3DUpgradeInteraction: boolean
  min3DConfigChanges: number

  forceRenderChain: boolean

  allow2DCustomWraps: boolean
  allowCustomWrapUpgrade: boolean

  appdata?: string
}

const DefaultServices : Record<string, string> = {
  "localhost": "slatestage",
  "configurator-dev": "slatestage",
  "configurator-test": "slatestage",
  "configurator-prelive": "slateprelive",
  "configurator-live": "slatelive"
}

export const DefaultService = DefaultServices[location.hostname.split(".")[0]] ?? DefaultServices["configurator-live"]

const DefaultArgs : IUrlArgs = {
  parentPath: `${window.location.origin}${window.location.pathname}`,
  locale: "en-US",
  assetService: DefaultService,
  shortcodeService: DefaultService,
  timeout: 40,
  forceCacheMiss: false,
  force2D: false,
  force3D: false,
  debug: false,
  showTranslationIds: false,
  lazyLoad: false,
  showPrices: false,
  itemsAutoApply: true,
  generateAssets: false,
  sharing: true,
  shareFile: false,
  focusedStreamInteraction: true,
  interiorInvertH: false,
  interiorInvertV: false,

  check3DUpgrade: true,
  check3DUpgradeRegion: true,
  min3DTimeZone: 3,
  max3DTimeZone: 10,
  lang3D: "en-US,es-US",
  check3DUpgradeInteraction: true,
  min3DConfigChanges: 40,

  forceRenderChain: false,


  allow2DCustomWraps: false, //DefaultService === "slatestage",
  allowCustomWrapUpgrade: true
}


const parseUrlParams = () => {
  const parser = new SearchParamsParser();

  parser.addParam("locale", ParamType.String, DefaultArgs.locale, "Localisation to use")

  parser.addParam("shortcode", ParamType.String, DefaultArgs.shortcode, "Initial configuration to load")
  parser.addParam("starterPack", ParamType.String, DefaultArgs.starterPack, "Starter Pack to load into")

  parser.addParam("from", ParamType.String, undefined, "Attribution id such as 'email' or 'microsite'")
  parser.addParam("micrositeId", ParamType.String, undefined, "Attribution id for the specific microsite")
  parser.addParam("shareId", ParamType.String, undefined, "Attribution id for the specific share instance")
  

  parser.addParam(
    "assetService",
    ParamType.String,
    DefaultArgs.assetService,
    "Render service to connect to"
  );

  parser.addParam(
    "shortcodeService",
    ParamType.String,
    undefined,
    "Allow using a separate shortcode service, defaults to assetService if not provided"
  )

  parser.addParam(
    "timeout",
    ParamType.Number,
    DefaultArgs.timeout,
    "How long before time out in seconds"
  );
  
  parser.addParam(
    "forceCacheMiss",
    ParamType.Bool,
    DefaultArgs.forceCacheMiss,
    "Force 2D requests to ignore the cache and force fresh generation"
  );

  parser.addParam(
    "force2D",
    ParamType.Bool,
    DefaultArgs.force2D,
    "Prevent upgrading to streaming"
  );

  parser.addParam(
    "force3D",
    ParamType.Bool,
    DefaultArgs.force2D,
    "Prevent downgrading to generation"
  );

  parser.addParam(
    "debug",
    ParamType.Bool,
    DefaultArgs.debug,
    "Pass true to enable debug function(s)"
  );

  parser.addParam(
    "showTranslationIds",
    ParamType.Bool,
    DefaultArgs.showTranslationIds,
    "Display the translation ID instead of the actual translation"
  );

  parser.addParam(
    "localStream",
    ParamType.String,
    DefaultArgs.localStream,
    "URL to the local stream"
  );

  parser.addParam(
    "overrideDate",
    ParamType.String,
    DefaultArgs.overrideDate,
    "Override the current date when considering validity in configuration data"
  )

  parser.addParam(
    "lazyLoad",
    ParamType.Bool,
    DefaultArgs.lazyLoad,
    "Should the turntable be generated straight away"
  )

  
  parser.addParam(
    "generateAssets",
    ParamType.Bool,
    DefaultArgs.generateAssets,
    "Generate items assets on load",
  )

  parser.addParam(
    "interiorInvertH",
    ParamType.Bool,
    DefaultArgs.interiorInvertH,
    "Invert interior horizontal axis",
  )

  parser.addParam(
    "interiorInvertV",
    ParamType.Bool,
    DefaultArgs.interiorInvertV,
    "Invert interior vertical axis",
  )

  parser.addParam(
    "focusedStreamInteraction",
    ParamType.Bool,
    DefaultArgs.focusedStreamInteraction,
    "Stream only can consume mouse wheel & pointer event only when focused",
  )

  parser.addParam("showPrices", ParamType.Bool, DefaultArgs.showPrices, "Show pricing information")
  parser.addParam("itemsAutoApply", ParamType.Bool, DefaultArgs.itemsAutoApply, "Automatically apply items on click")

  
  parser.addParam(
    "sharing",
    ParamType.Bool,
    DefaultArgs.sharing,
    "Should share buttons be enabled"
  )

  parser.addParam(
    "shareFile",
    ParamType.Bool,
    DefaultArgs.shareFile,
    "Should sharing a file be permitted"
  )

  parser.addParam(
    "configId",
    ParamType.String,
    undefined,
    "Slate defined ID to loading a saved build"
  )

  parser.addParam(
    "starterPack",
    ParamType.String,
    undefined,
    "ID for a predefined starter pack"
  )

  parser.addParam(
    "check3DUpgrade",
    ParamType.Bool,
    DefaultArgs.check3DUpgrade,
    "Check 3D upgrade conditions"
  )
  
  parser.addParam(
    "check3DUpgradeRegion",
    ParamType.Bool,
    DefaultArgs.check3DUpgradeRegion,
    "Check 3D upgrade conditions relating to location"
  )

  parser.addParam(
    "min3DTimeZone",
    ParamType.Number,
    DefaultArgs.min3DTimeZone,
    "Minimal GMT offset for 3D upgrade"
  )

  parser.addParam(
    "max3DTimeZone",
    ParamType.Number,
    DefaultArgs.max3DTimeZone,
    "Maximal GMT offset for 3D upgrade"
  )

  parser.addParam(
    "lang3D",
    ParamType.Bool,
    DefaultArgs.lang3D,
    "Navigator language allowed for 3D upgrade"
  )

  
  parser.addParam(
    "check3DUpgradeInteraction",
    ParamType.Bool,
    DefaultArgs.check3DUpgradeInteraction,
    "Check 3D upgrade conditions relating to interaction"
  )

  parser.addParam(
    "min3DInteractions",
    ParamType.Number,
    DefaultArgs.min3DConfigChanges,
    "Minimum number of user interactions for 3D upgrade"
  )

  parser.addParam(
    "forceRenderChain",
    ParamType.Bool,
    DefaultArgs.forceRenderChain,
    "Force using Render Chain to render save/share images"
  )

  parser.addParam(
    "allow2DCustomWraps",
    ParamType.Bool,
    DefaultArgs.allow2DCustomWraps,
    "Allow using the colour picker even in 2D"
  )
  
  parser.addParam(
    "allowCustomWrapUpgrade",
    ParamType.Bool,
    DefaultArgs.allowCustomWrapUpgrade,
    "Allow auto upgrading to 3d if the user selects the colour picker"
  )
  
  parser.addParam("appdata", ParamType.String, undefined, "Override location to find API data, used by the 'Preview' feature in Content Bundler")

  const args = parser.parse(window.location.search) as IUrlArgs
  if(args.overrideDate) {
    args.overrideDate = new Date(args.overrideDate)
  }

  return {...DefaultArgs, ...args}
}

const InitialArgs = parseUrlParams()
InitialArgs.shortcodeService ??= InitialArgs.assetService

console.log("Initial Url Params:", InitialArgs)

export const getInitialUrlArgs = () => InitialArgs;
export const isDebugFeature = (feature: string) => Boolean(window.localStorage && window.localStorage.getItem(`zl-debug-${feature}`) );
